import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Link as LinkIcon, RefreshCw, Unplug, Calendar, ArrowLeftRight } from "lucide-react";
import { formatET } from "@/lib/dates";

export default function GoogleCalendarPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/oauth/calendar/status");
      setStatus(data);
      if (data.connected) {
        const r = await api.get("/oauth/calendar/calendars");
        setCalendars(r.data.calendars || []);
      }
    } catch (err) {
      toast.error("Failed to load Google Calendar status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("gcal_connected") === "1") {
      toast.success("Google Calendar connected!");
      params.delete("gcal_connected");
      setParams(params, { replace: true });
    }
    if (params.get("gcal_error")) {
      toast.error(`Connection failed: ${params.get("gcal_error")}`);
      params.delete("gcal_error");
      setParams(params, { replace: true });
    }
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/calendar/start");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Google login");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Google Calendar? Future bookings will no longer sync.")) return;
    setBusy(true);
    try {
      await api.post("/oauth/calendar/disconnect");
      toast.success("Disconnected");
      await loadStatus();
    } catch (err) {
      toast.error("Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  const selectCalendar = async (cal_id) => {
    const cal = calendars.find((c) => c.id === cal_id);
    setBusy(true);
    try {
      await api.post("/oauth/calendar/select", { calendar_id: cal_id, calendar_name: cal?.summary });
      toast.success(`Bookings will sync to "${cal?.summary}"`);
      await loadStatus();
    } catch (err) {
      toast.error("Failed to select calendar");
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/calendar/sync-now");
      const r = data.result || {};
      toast.success(`Synced — pulled ${r.synced || 0} events, removed ${r.deleted || 0}`);
      await loadStatus();
    } catch (err) {
      toast.error("Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const fullResync = async () => {
    if (!window.confirm("Force a full resync? This wipes every previously-imported Google event and re-pulls fresh. Manual blocks and bookings inside Turf Booking are unaffected.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/calendar/full-resync");
      const r = data.result || {};
      toast.success(`Full resync done — purged ${data.purged || 0}, re-imported ${r.synced || 0} matching events`);
      await loadStatus();
    } catch (err) {
      toast.error("Full resync failed");
    } finally {
      setBusy(false);
    }
  };

  const forceResync = async () => {
    if (!window.confirm("This re-imports all events from the connected calendars (no purge — just clears the sync_token and pulls fresh). Continue?")) return;
    setBusy(true);
    try {
      // super_admin: hit "all" to do both brands at once. company_admin: backend will pick their brand.
      const isSuper = (user?.role === "super_admin");
      const url = isSuper ? "/oauth/calendar/force-resync?brand=all" : "/oauth/calendar/force-resync";
      const { data } = await api.post(url);
      if (isSuper && data?.result && typeof data.result === "object") {
        const rr = data.result.rrdc || {};
        const rv = data.result.riverview || {};
        toast.success(`Force resync — RRDC: ${rr.synced || 0} synced / ${rr.deleted || 0} deleted; Riverview: ${rv.synced || 0} synced / ${rv.deleted || 0} deleted`);
      } else {
        const r = data.result || {};
        toast.success(`Force resync — ${r.synced || 0} synced, ${r.deleted || 0} removed`);
      }
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Force resync failed");
    } finally {
      setBusy(false);
    }
  };

  const setColumnOverride = async (override) => {
    setBusy(true);
    try {
      await api.post("/oauth/calendar/column-override", { override });
      const label = override === "auto" ? "Auto-detect" : (override === "rrdc_field" ? "Rolling River Field" : "Turf House");
      toast.success(`Column set to: ${label}. Existing events re-classified.`);
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set column");
    } finally {
      setBusy(false);
    }
  };

  const swapBrands = async () => {
    if (!window.confirm(
      "SWAP the Google calendars between RRDC and Riverview brands?\n\n" +
      "Use this only if your calendars are flipped — events from rollingriver.com showing under Riverview, and vice versa.\n\n" +
      "This is permanent and updates both connections in the database. Existing imported events will be re-tagged to the correct brand."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/calendar/swap-brands");
      const r = data.sync_results || {};
      toast.success(
        `Swapped! RRDC pulled ${r.rrdc?.synced || 0}, Riverview pulled ${r.riverview?.synced || 0}.`
      );
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Swap failed");
    } finally {
      setBusy(false);
    }
  };

  const [peek, setPeek] = useState(null);
  const [peekLoading, setPeekLoading] = useState(false);
  const showPeek = async () => {
    setPeekLoading(true);
    try {
      const { data } = await api.get("/oauth/calendar/peek");
      setPeek(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not peek");
    } finally {
      setPeekLoading(false);
    }
  };

  const [extrasState, setExtrasState] = useState({ primary: null, extras: [] });
  const [extraCalToAdd, setExtraCalToAdd] = useState("");
  const [extraColumnHint, setExtraColumnHint] = useState("auto");
  const [manualCalId, setManualCalId] = useState("");
  const [manualCalName, setManualCalName] = useState("");

  const refreshCalendars = async () => {
    setBusy(true);
    try {
      const r = await api.get("/oauth/calendar/calendars");
      const list = r.data.calendars || [];
      setCalendars(list);
      toast.success(`Found ${list.length} calendars on this Google account`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not refresh calendars");
    } finally {
      setBusy(false);
    }
  };

  const addByCalendarId = async () => {
    const id = (manualCalId || "").trim();
    if (!id) { toast.error("Paste a Calendar ID"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/calendar/extras/add", {
        calendar_id: id,
        calendar_name: (manualCalName || "").trim() || id,
        column_hint: extraColumnHint === "auto" ? null : extraColumnHint,
      });
      const r = data.sync_result || {};
      toast.success(`Added by ID — pulled ${r.synced || 0} events`);
      setManualCalId("");
      setManualCalName("");
      setExtraColumnHint("auto");
      await loadExtras();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add calendar by ID");
    } finally {
      setBusy(false);
    }
  };

  const loadExtras = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/synced-list");
      setExtrasState({ primary: data.primary, extras: data.extras || [] });
    } catch { /* ignore */ }
  };

  useEffect(() => { if (status?.connected) loadExtras(); /* eslint-disable-next-line */ }, [status?.connected]);

  const addExtraCalendar = async () => {
    if (!extraCalToAdd) { toast.error("Pick a sub-calendar"); return; }
    const cal = calendars.find((c) => c.id === extraCalToAdd);
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/calendar/extras/add", {
        calendar_id: extraCalToAdd,
        calendar_name: cal?.summary || extraCalToAdd,
        column_hint: extraColumnHint === "auto" ? null : extraColumnHint,
      });
      const r = data.sync_result || {};
      toast.success(`Added "${cal?.summary}" — pulled ${r.synced || 0} new events`);
      setExtraCalToAdd("");
      setExtraColumnHint("auto");
      await loadExtras();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add calendar");
    } finally {
      setBusy(false);
    }
  };

  const removeExtraCalendar = async (cal_id, label) => {
    if (!window.confirm(`Stop syncing "${label}"? Events imported from this calendar will be removed.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/oauth/calendar/extras/${encodeURIComponent(cal_id)}`);
      toast.success(`Removed — ${data.blocks_removed || 0} events deleted from dashboard`);
      await loadExtras();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="google-calendar-settings">
        <div>
          <h1 className="text-3xl font-bold">Google Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">Two-way sync between Turf Booking and your Google Calendar.</p>
        </div>

        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {!loading && status && !status.configured && (
          <Card className="border-amber-500/40 bg-amber-500/5">
            <CardContent className="p-6">
              <p className="text-sm text-amber-600">Google Calendar credentials are not configured on the server. Contact your administrator.</p>
            </CardContent>
          </Card>
        )}

        {!loading && status?.configured && !status.connected && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar size={20} /> Connect your Google Calendar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Sign in with the Google account that owns the calendar this brand should sync to. Bookings created here will appear there, and events on that calendar will block availability here.
              </p>
              <Button onClick={connect} disabled={busy} data-testid="gcal-connect-btn">
                <LinkIcon size={16} /> {busy ? "Redirecting…" : "Connect Google Calendar"}
              </Button>
            </CardContent>
          </Card>
        )}

        {!loading && status?.connected && (
          <>
            {user?.role === "super_admin" && (
              <Card className="border-amber-500/40 bg-amber-500/5" data-testid="gcal-swap-card">
                <CardContent className="p-5 flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <ArrowLeftRight size={18} className="text-amber-500" />
                      <span className="font-medium">Calendars flipped?</span>
                      <Badge variant="outline" className="text-[10px]">super admin</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                      If RRDC events are showing under the Riverview connection (or vice-versa), this swaps which Google calendar each brand reads from. Permanent — updates both connections in the database and re-tags existing imported events. Use once, problem solved.
                    </p>
                  </div>
                  <Button onClick={swapBrands} disabled={busy} variant="outline" className="border-amber-500/60 text-amber-500 hover:text-amber-400" data-testid="gcal-swap-brands-btn">
                    <ArrowLeftRight size={14} /> Swap RRDC ⇄ Riverview
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card className="border-emerald-500/40 bg-emerald-500/5">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={20} className="text-emerald-500" />
                      <span className="font-medium">Connected</span>
                      <Badge variant="outline">{status.brand}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Account: <span className="font-medium">{status.email}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Calendar: <span className="font-medium">{status.calendar_name || status.calendar_id}</span>
                    </p>
                    {status.last_synced_at && (
                      <p className="text-xs text-muted-foreground mt-1">Last sync: {formatET(status.last_synced_at)}</p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={syncNow} disabled={busy} data-testid="gcal-sync-now-btn">
                      <RefreshCw size={14} /> Sync now
                    </Button>
                    <Button variant="outline" onClick={forceResync} disabled={busy} data-testid="gcal-force-resync-btn">
                      <RefreshCw size={14} /> Force full resync
                    </Button>
                    <Button variant="outline" onClick={fullResync} disabled={busy} data-testid="gcal-full-resync-btn">
                      <RefreshCw size={14} /> Wipe & resync
                    </Button>
                    <Button variant="outline" onClick={disconnect} disabled={busy} data-testid="gcal-disconnect-btn">
                      <Unplug size={14} /> Disconnect
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Which column do these events go to?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Force every event from this Google calendar into a specific column on the booking calendar.
                  This overrides the automatic detection. Changing this will re-classify all existing imported events instantly.
                </p>
                <Select
                  value={status.column_override || "auto"}
                  onValueChange={setColumnOverride}
                  disabled={busy}
                >
                  <SelectTrigger data-testid="gcal-column-override-select" className="w-full md:w-[420px]">
                    <SelectValue placeholder="Choose column" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-detect (default)</SelectItem>
                    <SelectItem value="riverview_split">🟣 Riverview Split (Rink/Courts/Field by event title)</SelectItem>
                    <SelectItem value="rrdc_field">🔵 Rolling River Field column</SelectItem>
                    <SelectItem value="rrdc_rink">🧊 Rolling River Rink column</SelectItem>
                    <SelectItem value="rrdc_courts">🏀 Rolling River Courts column</SelectItem>
                    <SelectItem value="turf_house">🟢 Turf House column</SelectItem>
                  </SelectContent>
                </Select>
                {status.column_override && status.column_override !== "auto" && (
                  <p className="text-xs text-amber-500">
                    Manual override active: events from this calendar always land in the {status.column_override === "rrdc_field" ? "Rolling River Field" : "Turf House"} column.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card data-testid="extras-card">
              <CardHeader>
                <CardTitle className="text-lg">Additional sub-calendars to sync</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Pull events from <b>multiple sub-calendars</b> on this same Google account into the dashboard. Pick a column hint to pin every event from that sub-calendar to a specific dashboard column.
                </p>

                {/* List currently-synced extras */}
                {extrasState.extras.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No additional sub-calendars added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {extrasState.extras.map((ex) => (
                      <div key={ex.id} className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 bg-muted/30" data-testid={`extra-${ex.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{ex.name || ex.id}</p>
                          <p className="text-[11px] text-muted-foreground font-mono truncate">{ex.id}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px]">
                          {ex.column_hint ? ex.column_hint.replace("_", " ").toUpperCase() : "AUTO"}
                        </Badge>
                        <Button variant="ghost" size="sm" onClick={() => removeExtraCalendar(ex.id, ex.name || ex.id)} data-testid={`remove-extra-${ex.id}`}>
                          <XCircle size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new */}
                <div className="border-t border-border pt-4 space-y-3">
                  <p className="text-sm font-medium">Add a sub-calendar</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      Don't see your calendar in the list?
                    </p>
                    <Button variant="outline" size="sm" onClick={refreshCalendars} disabled={busy} data-testid="refresh-calendars-btn">
                      <RefreshCw size={12} /> Refresh list
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Select value={extraCalToAdd} onValueChange={setExtraCalToAdd}>
                      <SelectTrigger data-testid="add-extra-cal-select" className="md:col-span-2">
                        <SelectValue placeholder="Pick a sub-calendar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {calendars
                          .filter((c) => c.id !== status.calendar_id && !extrasState.extras.some((e) => e.id === c.id))
                          .map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.summary}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Select value={extraColumnHint} onValueChange={setExtraColumnHint}>
                      <SelectTrigger data-testid="add-extra-col-hint">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Column: Auto-detect</SelectItem>
                        <SelectItem value="turf_house">Column: Turf House</SelectItem>
                        <SelectItem value="rrdc_field">Column: Rolling River Field</SelectItem>
                        <SelectItem value="rrdc_rink">Column: Rolling River Rink</SelectItem>
                        <SelectItem value="rrdc_courts">Column: Rolling River Courts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addExtraCalendar} disabled={busy || !extraCalToAdd} data-testid="add-extra-cal-btn">
                    <Calendar size={14} /> Add sub-calendar
                  </Button>
                </div>

                {/* Manual add by Calendar ID (fallback when dropdown doesn't list it) */}
                <div className="border-t border-border pt-4 space-y-3">
                  <p className="text-sm font-medium">Add by Calendar ID</p>
                  <p className="text-xs text-muted-foreground">
                    If a calendar (e.g. <b>Riverview Athletics</b>) doesn't appear above,
                    find its Calendar ID in Google Calendar → <i>Settings → Integrate calendar → Calendar ID</i>
                    (looks like <code className="text-[10px]">xxx@group.calendar.google.com</code>) and paste it here.
                    The connected Google account must be subscribed to it.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Input
                      placeholder="xxxxxxxx@group.calendar.google.com"
                      value={manualCalId}
                      onChange={(e) => setManualCalId(e.target.value)}
                      data-testid="manual-cal-id-input"
                    />
                    <Input
                      placeholder="Friendly name (e.g. Riverview Athletics)"
                      value={manualCalName}
                      onChange={(e) => setManualCalName(e.target.value)}
                      data-testid="manual-cal-name-input"
                    />
                  </div>
                  <Button onClick={addByCalendarId} disabled={busy || !manualCalId.trim()} data-testid="add-by-id-btn">
                    <LinkIcon size={14} /> Add by ID
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Why isn't my event syncing?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Show the next ~25 events on this Google calendar plus exactly which would be imported, which would be skipped, and why.
                </p>
                <Button variant="outline" onClick={showPeek} disabled={peekLoading || busy} data-testid="gcal-peek-btn">
                  {peekLoading ? "Loading…" : "Show me what's on this calendar"}
                </Button>
                {peek && (
                  <div className="mt-4 border rounded-lg p-4 space-y-3 bg-muted/20" data-testid="gcal-peek-output">
                    <div className="text-xs font-mono space-y-0.5">
                      <p>Calendar name: <span className="text-foreground">{peek.routing?.calendar_summary || "—"}</span></p>
                      <p>Calendar id: <span className="text-foreground">{peek.calendar_id}</span></p>
                      <p>Override: <span className="text-foreground">{peek.column_override}</span></p>
                      <p>Routes to: <span className="text-foreground font-bold">{peek.routing?.auto_detected_column}</span> ({peek.routing?.route_reason})</p>
                      <p>Keyword filter: <span className="text-foreground">{(peek.routing?.keyword_filter || []).join(", ")}</span></p>
                    </div>
                    <hr className="border-border"/>
                    {(peek.events || []).length === 0 && (
                      <p className="text-sm text-muted-foreground">No events found in the next 6 months on this calendar.</p>
                    )}
                    <div className="space-y-1.5 max-h-96 overflow-y-auto">
                      {(peek.events || []).map((ev) => (
                        <div key={ev.id} className="text-xs flex items-start gap-2 py-1 border-b border-border/40">
                          <span className={`mt-0.5 inline-block w-2 h-2 rounded-full shrink-0 ${ev.would_import ? "bg-emerald-500" : "bg-rose-500"}`} />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">{ev.title || "(no title)"}</p>
                            <p className="text-muted-foreground font-mono text-[10px]">
                              {ev.start} {ev.all_day ? "· all-day (skipped)" : ""}
                              {!ev.passes_keyword_filter && ` · skipped — title doesn't contain ${(peek.routing?.keyword_filter || []).join("/")}`}
                              {ev.would_import && ` · → ${ev.would_route_to}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground italic">
                      Green dot = imported · Red dot = skipped (all-day or keyword filter)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Choose calendar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">Pick which calendar on this Google account should hold your turf bookings.</p>
                <Select
                  value={status.calendar_id || "primary"}
                  onValueChange={selectCalendar}
                  disabled={busy}
                >
                  <SelectTrigger data-testid="gcal-calendar-select" className="w-full md:w-[420px]">
                    <SelectValue placeholder="Select a calendar" />
                  </SelectTrigger>
                  <SelectContent>
                    {calendars.map((c) => (
                      <SelectItem key={c.id} value={c.id} disabled={c.access_role === "reader" || c.access_role === "freeBusyReader"}>
                        {c.summary} {c.primary ? "(primary)" : ""} {c.access_role === "reader" || c.access_role === "freeBusyReader" ? "— read only" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          </>
        )}

        <Card className="bg-muted/20">
          <CardContent className="p-6 text-sm text-muted-foreground space-y-2">
            <p className="font-medium text-foreground">How it works</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Each brand connects its own Google account — sign in as that brand's admin first.</li>
              <li>New inquiries, manual blocks, contracts, and bookings push events to Google.</li>
              <li>Events created/edited directly in Google Calendar are pulled in and shown as "external busy" so they block availability.</li>
              <li>Cancelling, releasing, or refunding a booking removes the matching Google event.</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
